// src/services/PasswordService.ts

import CryptoService from './CryptoService';
import CryptoUtilities from './CryptoUtilities';

/**
 * Classe per gestire le operazioni relative alle password, inclusa la crittografia e la verifica.
 */
export class PasswordService {
  private cryptoService: CryptoService;

  constructor() {
    this.cryptoService = CryptoService.getInstance();
  }

  /**
   * Crea un hash della password combinando la password e un sale.
   * @param password - La password da crittografare.
   * @param salt - Il sale da utilizzare per l'hashing.
   * @returns La password hashata.
   */
  public async hashPassword(
    password: string,
    salt: ArrayBuffer
  ): Promise<ArrayBuffer> {
    // In una reale implementazione, usa una funzione di hashing delle password adeguata come bcrypt o Argon2
    return await this.cryptoService.hashSHA256(
      CryptoUtilities.concatArrayBuffer(
        CryptoUtilities.UTF8StringToArrayBuffer(password),
        salt
      )
    );
  }

  /**
   * Genera un sale casuale per l'hashing delle password.
   * @returns Un sale casuale.
   */
  public async generateSalt(): Promise<ArrayBuffer> {
    return await this.cryptoService.generateIV();
  }

  /**
   * Verifica se una password corrisponde a una password hashata.
   * @param password - La password da verificare.
   * @param salt - Il sale utilizzato per l'hashing.
   * @param hashedPassword - La password hashata da confrontare.
   * @returns True se la password è corretta, altrimenti false.
   */
  public async verifyPassword(
    password: string,
    salt: ArrayBuffer,
    hashedPassword: ArrayBuffer
  ): Promise<boolean> {
    const calculatedHash = await this.hashPassword(password, salt);
    return CryptoUtilities.arrayBufferEquals(calculatedHash, hashedPassword);
  }
}

export type PasswordOptions = {
  useNumbers?: boolean;
  useSymbols?: boolean;
  useLowercase?: boolean;
  useUppercase?: boolean;
  passwordLength?: number;
};
export async function generatePassword({
  useNumbers = true,
  useSymbols = true,
  useLowercase = true,
  useUppercase = true,
  passwordLength = 16,
}: PasswordOptions): Promise<string> {
  const charset = {
    numbers: '0123456789',
    symbols: '!@#$%^&*()_+[]{}|;:,.<>?',
    lowercase: 'abcdefghijklmnopqrstuvwxyz',
    uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  };

  let passwordCharset = '';

  if (useNumbers) passwordCharset += charset.numbers;
  if (useSymbols) passwordCharset += charset.symbols;
  if (useLowercase) passwordCharset += charset.lowercase;
  if (useUppercase) passwordCharset += charset.uppercase;

  if (passwordCharset.length === 0) {
    throw new Error('Deve essere selezionato almeno un set di caratteri');
  }

  const passGen = await CryptoService.getInstance().generateRandomValues(
    passwordLength
  );

  let newPassword = '';
  for (let i = 0; i < passGen.length; i++) {
    newPassword += passwordCharset.charAt(passGen[i] % passwordCharset.length);
  }
  // passwordCharset =  'abcdefghijklmnopqrstuvwxyz'
  // passGen = [5, 10, 34, 56, 89]
  // newPassword  = "" => "f" => "fk" => "fki"=> "fkie" => "fkiel"

  return newPassword;
}

export default PasswordService;
